import { useState, useEffect, ReactNode } from 'react'
import axios from 'axios'
import Cookies from 'js-cookie'
import { config } from '../../config'
import { AuthContextMain, LoginResult, MainUser } from './UseAuthContextMain'

export const AuthProviderMain = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<MainUser | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Check for existing session on mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const { data } = await axios.get(`${config.server}/main/verify-token`, {
          withCredentials: true,
        })
        setUser(data.user)
        Cookies.set('isLoggedInMain', 'true', {
          expires: 1,
          path: '/',
          domain: 'localhost',
          sameSite: 'lax',
        })
        await refreshUser()
        console.log('[AuthContextMain] User verified:', {
          id: data.user?.id,
          email: data.user?.email,
          permissions: data.user?.permissions
        })
        setLoading(false)
      } catch (err) {
        const errorMsg = (err instanceof Error ? err.message : String(err)) || ''
        setError(errorMsg)
        if (errorMsg === 'Network Error') {
          setTimeout(() => {
            // Retry when backend is offline
            verifyToken()
          }, 3000)
        } else {
          setUser(null)
          Cookies.remove('isLoggedInMain', {
            path: '/',
            domain: 'localhost',
          })
          setLoading(false)
        }
      }
    }

    verifyToken() // Always verify on mount
  }, [])

  const checkAdminStatus = async () => {
    try {
      if (!user) {
        setIsAdmin(false)
        return false
      }

      const { data } = await axios.get(`${config.server}/main/verify-admin`, {
        withCredentials: true,
      })

      setIsAdmin(data.isAdmin)
      return data.isAdmin
    } catch (error) {
      console.error(new Date(), 'Admin check error:', error) // Debug log
      setIsAdmin(false)
      return false
    }
  }

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${config.server}/main/verify-token`, {
        withCredentials: true,
      })
      if (data.user) {
        setUser({
          ...data.user,
          permissions: data.user.permissions, // Ensure permissions are set
        })
        setIsAuthenticated(true)

        // Immediately check admin status when user is loaded
        if (data.user.permissions === 'admin') {
          const adminCheck = await checkAdminStatus()
          setIsAdmin(adminCheck)
        } else {
          setIsAdmin(false)
        }
      } else {
        setUser(null)
        setIsAuthenticated(false)
        setIsAdmin(false)
      }
    } catch (error) {
      setUser(null)
      setIsAuthenticated(false)
      setIsAdmin(false)
    }
  }

  const register = async (data: { username: string; email: string; password: string; full_name: string }) => {
    try {
      console.log('Starting registration process for:', data.email);

      // Register the user without auto-login
      const response = await axios.post(`${config.server}/main/register`, data, {
        withCredentials: true,
      });

      console.log('Registration response received:', {
        skipVerification: response.data.skipVerification,
        verified: response.data.user?.verified,
        redirectTo: response.data.redirectTo
      });

      // In development mode or if verification is skipped, the user is automatically logged in
      if (response.data.skipVerification || (response.data.user && response.data.user.verified === true)) {
        console.log('Registration successful with auto-login, refreshing user state');

        // Refresh user state to update auth context after registration
        await refreshUser();

        // Set cookie only after successful registration with auto-login
        Cookies.set('isLoggedInMain', 'true', {
          expires: 1,
          path: '/',
          sameSite: 'lax',
        });

        console.log('User refreshed and cookies set for newly registered user');
      }

      // Return the response directly
      return response.data;
    } catch (error: unknown) {
      const responseError = error as {
        response?: {
          data?: {
            error?: string;
            emailDeliveryFailed?: boolean;
          }
        }
      }

      // If the error is related to email delivery, propagate the flag
      if (responseError?.response?.data?.emailDeliveryFailed) {
        throw new Error(responseError.response.data.error || 'Email delivery failed');
      }

      throw new Error((responseError && 'response' in responseError && responseError.response?.data?.error) || 'Registration failed')
    }
  }

  const login = async ({ email, password }: { email: string; password: string }): Promise<LoginResult> => {
    try {
      const response = await axios.post(
        `${config.server}/main/login`,
        {
          email,
          password,
        },
        {
          withCredentials: true,
        }
      )

      if (response.status === 200 && response.data.success) {
        // Check if this response requires additional verification steps
        if (response.data.requires2FA || response.data.requiresEmailVerification) {
          // Don't call refreshUser() or set cookies yet - wait for verification to complete
          console.log('Login requires additional verification. Skipping automatic refreshUser().');

          // Return the verification requirements
          return {
            success: true,
            requires2FA: response.data.requires2FA,
            requiresEmailVerification: response.data.requiresEmailVerification,
            email: response.data.email,
            userId: response.data.userId,
            sessionToken: response.data.sessionToken,
            authMethodPreference: response.data.authMethodPreference
          };
        }

        // Normal successful login
        setUser(response.data.user)
        setIsAuthenticated(true)
        await refreshUser()

        // Set cookie only after successful login
        Cookies.set('isLoggedInMain', 'true', {
          expires: 1,
          path: '/',
          sameSite: 'lax',
        })

        return { success: true }
      } else {
        return { success: false, error: response.data.error || 'Unknown error' }
      }
    } catch (error) {
      let msgerr: string | unknown = 'Login error'
      if (error instanceof Error) {
        msgerr = error.message
      } else {
        msgerr = error
      }
      console.error(new Date(), 'Login error:', msgerr)

      const responseError = error as {
        response?: {
          data?: {
            error?: string;
            needsVerification?: boolean;
            email?: string;
            requiresEmailVerification?: boolean;
            requires2FA?: boolean;
            userId?: number;
            sessionToken?: string;
            authMethodPreference?: string;
          }
        }
      }

      // Handle 2FA required response
      if (responseError?.response?.data?.requires2FA) {
        return {
          success: true,
          requires2FA: true,
          email: responseError.response.data.email,
          userId: responseError.response.data.userId,
          sessionToken: responseError.response.data.sessionToken,
          authMethodPreference: responseError.response.data.authMethodPreference
        }
      }

      // Handle verification needed response for registration
      if (responseError?.response?.data?.needsVerification) {
        return {
          success: false,
          needsVerification: true,
          email: responseError.response.data.email,
          error: responseError.response.data.error
        }
      }

      // Handle email verification for login
      if (responseError?.response?.data?.requiresEmailVerification) {
        return {
          success: true,
          requiresEmailVerification: true,
          email: responseError.response.data.email,
          userId: responseError.response.data.userId,
          sessionToken: responseError.response.data.sessionToken,
          authMethodPreference: responseError.response.data.authMethodPreference
        }
      }

      return {
        success: false,
        error: (responseError && 'response' in responseError && responseError.response?.data?.error) || 'Login failed',
      }
    }
  }

  const logout = async () => {
    try {
      await axios.post(
        `${config.server}/main/logout`,
        {},
        {
          withCredentials: true,
        }
      )
      setUser(null)
      setIsAuthenticated(false)
      Cookies.remove('isLoggedInMain')
    } catch (error) {
      console.error(new Date(), 'Logout failed:', error)
    }
  }

  // Add new methods for email verification
  const verifyEmail = async (data: { email: string; code: string }) => {
    try {
      const response = await axios.post(
        `${config.server}/main/verify-email`,
        data,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.verified) {
        await refreshUser();
        return { success: true };
      }

      return { success: false, error: 'Verification failed' };
    } catch (error: unknown) {
      const responseError = error as {
        response?: {
          data?: {
            error?: string
          }
        }
      }
      return {
        success: false,
        error: (responseError && 'response' in responseError && responseError.response?.data?.error) || 'Verification failed'
      };
    }
  }

  const resendVerification = async (email: string) => {
    try {
      const response = await axios.post(
        `${config.server}/main/resend-verification`,
        { email },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.status === 200) {
        return { success: true };
      }
      return { success: false, error: response.data?.error || 'Failed to resend verification' };
    } catch (error: unknown) {
      const responseError = error as {
        response?: {
          data?: {
            error?: string
          }
        }
      }
      return {
        success: false,
        error: (responseError && 'response' in responseError && responseError.response?.data?.error) || 'Failed to resend verification'
      };
    }
  }

  if (loading && error) {
    return (
      <div className='flex justify-center items-center min-h-screen'>
        <div className='p-4 text-red-500 bg-red-100 rounded-lg shadow-md'>{error === 'Network Error' ? error + ' - The backend is possibly offline!' : error}</div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className='flex justify-center items-center min-h-screen'>
        <div className='animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-purple-500'></div>
      </div>
    )
  }

  return (
    <AuthContextMain.Provider
      value={{
        user,
        login,
        logout,
        register,
        refreshUser,
        isAuthenticated,
        isAdmin,
        checkAdminStatus,
        verifyEmail,
        resendVerification
      }}
    >
      {children}
    </AuthContextMain.Provider>
  )
}

